import React, { useState, useEffect, useMemo } from 'react';
import {
    Card, Button, Table, Modal, Input, Select, InputNumber, Switch,
    message, Popconfirm, Space, Tag, Row, Col, Tabs, Empty, Spin,
    Checkbox, Divider, DatePicker, Tooltip
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ArrowRightOutlined,
    CheckCircleOutlined, ClockCircleOutlined, TeamOutlined, SyncOutlined
} from '@ant-design/icons';
import MeService from '../../services/MeService';
import ProgramsService from '../../services/ProgramsService';
import { trailCreate, trailUpdate, trailDelete, logTrail, TRAIL_EVENTS } from '../../utils/activityTrail';
import moment from 'moment';

const { TextArea } = Input;
const { Option } = Select;

const FRAMEWORK_CODE = 'MOHPERF';

const SECTION_COLORS = {
    STRAT:'#6366f1', KPI:'#0ea5e9', ACT:'#f59e0b',
    ACH:'#10b981', CHA:'#ef4444', FWD:'#8b5cf6',
};

const INDICATOR_TYPES = ['requirement','output','outcome','process','milestone','kpi'];
const MEASUREMENT_UNITS = ['yes_no','percentage','count','score','date','text','currency'];
const FREQUENCIES = ['once','monthly','quarterly','biannual','annual'];

const CATEGORY_OPTIONS = ['program','unit','department'];
const REPORT_CATEGORY_OPTIONS = [
    'Administration','Programs','Legal','Planning','Strategic Information',
    'Quality & Standards','Hospitals','Regional',
];
const REGION_OPTIONS = ['Hhohho','Manzini','Lubombo','Shiselweni'];

const STATUS_COLORS = {
    not_started:'default', in_progress:'processing', on_track:'processing',
    delayed:'warning', completed:'success', certified:'success', suspended:'error',
};

// ── Indicator Catalog Tab ─────────────────────────────────────────────────
function IndicatorCatalogTab({ frameworkId }) {
    const [sections, setSections]     = useState([]);
    const [activeSection, setActiveSection] = useState(null);
    const [indicators, setIndicators] = useState([]);
    const [loading, setLoading]       = useState(false);
    const [modalOpen, setModalOpen]   = useState(false);
    const [editing, setEditing]       = useState(null);
    const [form, setForm]             = useState({});
    const [saving, setSaving]         = useState(false);

    const loadSections = async () => {
        if (!frameworkId) return;
        try {
            const r = await MeService.getSections(frameworkId);
            const secs = (r.data?.data || []).sort((a,b)=>a.display_order-b.display_order);
            setSections(secs);
            if (secs.length && !activeSection) setActiveSection(secs[0].id);
        } catch { message.error('Failed to load sections'); }
    };

    const loadIndicators = async (sectionId) => {
        if (!sectionId) { setIndicators([]); return; }
        setLoading(true);
        try {
            const r = await MeService.getIndicators({ section_id: sectionId, framework_id: frameworkId });
            setIndicators(r.data?.data || []);
        } catch { message.error('Failed to load indicators'); }
        finally { setLoading(false); }
    };

    useEffect(() => { loadSections(); }, [frameworkId]);
    useEffect(() => { loadIndicators(activeSection); }, [activeSection]);

    const openAdd = () => {
        setEditing(null);
        setForm({
            section_id: activeSection, framework_id: frameworkId,
            indicator_type: 'kpi', measurement_unit: 'percentage', frequency: 'quarterly',
            is_mandatory: 1, is_critical: 0, weight: 1,
        });
        setModalOpen(true);
    };
    const openEdit = (ind) => { setEditing(ind); setForm({ ...ind }); setModalOpen(true); };

    const save = async () => {
        if (!form.title?.trim()) { message.error('Indicator title is required'); return; }
        setSaving(true);
        try {
            await MeService.saveIndicator({ ...form, section_id: activeSection, framework_id: frameworkId });
            const section = sections.find(s => s.id === activeSection);
            if (editing) {
                trailUpdate('MoH Reporting', `Indicator: ${form.title}`, editing.id, { section: section?.title });
            } else {
                trailCreate('MoH Reporting', `Indicator: ${form.title}`, '', { section: section?.title });
            }
            message.success('Indicator saved');
            setModalOpen(false);
            loadIndicators(activeSection);
        } catch (e) { message.error('Failed to save indicator'); }
        finally { setSaving(false); }
    };

    const remove = async (ind) => {
        try {
            await MeService.deleteIndicator(ind.id);
            trailDelete('MoH Reporting', `Indicator: ${ind.title}`, ind.id);
            message.success('Indicator deleted');
            loadIndicators(activeSection);
        } catch { message.error('Failed to delete indicator'); }
    };

    const cols = [
        { title:'Code', dataIndex:'code', width:90, render:c=>c?<Tag>{c}</Tag>:'—' },
        { title:'Indicator', dataIndex:'title', render:(t,r)=>(
            <div>
                <div style={{fontWeight:600}}>{t}</div>
                {r.data_source && <div style={{fontSize:11,color:'#888'}}>Source: {r.data_source}</div>}
            </div>
        ) },
        { title:'Type', dataIndex:'indicator_type', width:110, render:t=><Tag color="blue">{t}</Tag> },
        { title:'Unit', dataIndex:'measurement_unit', width:100 },
        { title:'Baseline', dataIndex:'baseline_value', width:90, render:v=>v||'—' },
        { title:'Target', dataIndex:'target_value', width:90, render:v=>v||'—' },
        { title:'Frequency', dataIndex:'frequency', width:100 },
        { title:'Critical', dataIndex:'is_critical', width:80, render:v=>v?<Tag color="red">Critical</Tag>:null },
        { title:'', width:90, render:(_,r)=>(
            <Space>
                <Button size="small" icon={<EditOutlined/>} onClick={()=>openEdit(r)}/>
                <Popconfirm title="Delete this indicator?" onConfirm={()=>remove(r)}>
                    <Button size="small" danger icon={<DeleteOutlined/>}/>
                </Popconfirm>
            </Space>
        ) },
    ];

    return (
        <div>
            <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
                {sections.map(s => (
                    <Button key={s.id}
                        type={activeSection===s.id?'primary':'default'}
                        style={activeSection===s.id?{background:SECTION_COLORS[s.code],borderColor:SECTION_COLORS[s.code]}:{}}
                        onClick={()=>setActiveSection(s.id)}>
                        <span style={{fontWeight:700,marginRight:6}}>{s.code}</span>{s.title}
                    </Button>
                ))}
            </div>

            {activeSection && (
                <>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                        <div style={{ color:'#888', fontSize:12 }}>
                            {sections.find(s=>s.id===activeSection)?.description}
                        </div>
                        <Button type="primary" icon={<PlusOutlined/>} onClick={openAdd}>Add Indicator</Button>
                    </div>
                    <Table columns={cols} dataSource={indicators} rowKey="id" loading={loading}
                        pagination={false} size="small"
                        locale={{ emptyText: <Empty description="No indicators yet in this section"/> }}/>
                </>
            )}

            <Modal title={editing ? 'Edit Indicator' : 'Add Indicator'} open={modalOpen}
                onCancel={()=>setModalOpen(false)} onOk={save} confirmLoading={saving} width={640}>
                <Row gutter={12}>
                    <Col span={6}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Code</label>
                        <Input value={form.code} onChange={e=>setForm({...form,code:e.target.value})} placeholder="e.g. KPI.1"/>
                    </Col>
                    <Col span={18}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Indicator Title *</label>
                        <Input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g. PrEP initiations this quarter"/>
                    </Col>
                    <Col span={24} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Description</label>
                        <TextArea rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/>
                    </Col>
                    <Col span={8} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Type</label>
                        <Select style={{width:'100%'}} value={form.indicator_type} onChange={v=>setForm({...form,indicator_type:v})}>
                            {INDICATOR_TYPES.map(t=><Option key={t} value={t}>{t}</Option>)}
                        </Select>
                    </Col>
                    <Col span={8} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Measurement Unit</label>
                        <Select style={{width:'100%'}} value={form.measurement_unit} onChange={v=>setForm({...form,measurement_unit:v})}>
                            {MEASUREMENT_UNITS.map(t=><Option key={t} value={t}>{t}</Option>)}
                        </Select>
                    </Col>
                    <Col span={8} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Frequency</label>
                        <Select style={{width:'100%'}} value={form.frequency} onChange={v=>setForm({...form,frequency:v})}>
                            {FREQUENCIES.map(t=><Option key={t} value={t}>{t}</Option>)}
                        </Select>
                    </Col>
                    <Col span={8} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Baseline Value</label>
                        <Input value={form.baseline_value} onChange={e=>setForm({...form,baseline_value:e.target.value})}/>
                    </Col>
                    <Col span={8} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Target Value</label>
                        <Input value={form.target_value} onChange={e=>setForm({...form,target_value:e.target.value})}/>
                    </Col>
                    <Col span={8} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Weight</label>
                        <InputNumber style={{width:'100%'}} step={0.5} value={form.weight} onChange={v=>setForm({...form,weight:v})}/>
                    </Col>
                    <Col span={12} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Responsible Role / Unit</label>
                        <Input value={form.responsible_role} onChange={e=>setForm({...form,responsible_role:e.target.value})} placeholder="e.g. HR Core Unit"/>
                    </Col>
                    <Col span={12} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Data Source</label>
                        <Input value={form.data_source} onChange={e=>setForm({...form,data_source:e.target.value})} placeholder="e.g. HRIS, eLMIS, QoC surveys"/>
                    </Col>
                    <Col span={12} style={{marginTop:14}}>
                        <Checkbox checked={!!form.is_mandatory} onChange={e=>setForm({...form,is_mandatory:e.target.checked?1:0})}>Mandatory</Checkbox>
                    </Col>
                    <Col span={12} style={{marginTop:14}}>
                        <Checkbox checked={!!form.is_critical} onChange={e=>setForm({...form,is_critical:e.target.checked?1:0})}>Critical (flag if off-track)</Checkbox>
                    </Col>
                </Row>
            </Modal>
        </div>
    );
}

// ── Submitting Units Tab ──────────────────────────────────────────────────
function SubmittingUnitsTab() {
    const [units, setUnits]     = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm]       = useState({});
    const [saving, setSaving]   = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await ProgramsService.getPrograms();
            setUnits(r.data?.data || []);
        } catch { message.error('Failed to load submitting units'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openAdd = () => { setEditing(null); setForm({ category:'unit', status:1 }); setModalOpen(true); };
    const openEdit = (u) => { setEditing(u); setForm({ ...u }); setModalOpen(true); };

    const save = async () => {
        if (!form.name?.trim()) { message.error('Unit name is required'); return; }
        if (!form.code?.trim()) { message.error('A short unique code is required'); return; }
        setSaving(true);
        try {
            await ProgramsService.saveProgram(form);
            if (editing) trailUpdate('MoH Reporting', `Unit: ${form.name}`, editing.id);
            else trailCreate('MoH Reporting', `Unit: ${form.name}`, '');
            message.success('Unit saved');
            setModalOpen(false);
            load();
        } catch (e) { message.error(e?.message || 'Failed to save unit — code may already be in use'); }
        finally { setSaving(false); }
    };

    const remove = async (u) => {
        try {
            await ProgramsService.deleteProgram(u.id);
            trailDelete('MoH Reporting', `Unit: ${u.name}`, u.id);
            message.success('Unit deleted');
            load();
        } catch { message.error('Failed to delete unit'); }
    };

    const cols = [
        { title:'Name', dataIndex:'name', render:(n,r)=><div><div style={{fontWeight:600}}>{n}</div><div style={{fontSize:11,color:'#888'}}>{r.code}</div></div> },
        { title:'Category', dataIndex:'category', width:110, render:c=><Tag>{c}</Tag> },
        { title:'Tracker Group', dataIndex:'report_category', width:160, render:c=>c?<Tag color="blue">{c}</Tag>:<span style={{color:'#ccc'}}>—</span> },
        { title:'Region', dataIndex:'region', width:110, render:r=>r||<span style={{color:'#ccc'}}>National</span> },
        { title:'Status', dataIndex:'status', width:90, render:s=>s?<Tag color="green">Active</Tag>:<Tag>Inactive</Tag> },
        { title:'', width:90, render:(_,r)=>(
            <Space>
                <Button size="small" icon={<EditOutlined/>} onClick={()=>openEdit(r)}/>
                <Popconfirm title="Delete this unit? This also removes its report assignments and submitted data." onConfirm={()=>remove(r)}>
                    <Button size="small" danger icon={<DeleteOutlined/>}/>
                </Popconfirm>
            </Space>
        ) },
    ];

    return (
        <div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                <div style={{ color:'#888', fontSize:12 }}>{units.length} submitting units registered</div>
                <Button type="primary" icon={<PlusOutlined/>} onClick={openAdd}>Add Unit</Button>
            </div>
            <Table columns={cols} dataSource={units} rowKey="id" loading={loading} pagination={{pageSize:20}} size="small"/>

            <Modal title={editing ? 'Edit Submitting Unit' : 'Add Submitting Unit'} open={modalOpen}
                onCancel={()=>setModalOpen(false)} onOk={save} confirmLoading={saving} width={520}>
                <Row gutter={12}>
                    <Col span={16}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Unit Name *</label>
                        <Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. Procurement Unit"/>
                    </Col>
                    <Col span={8}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Short Code *</label>
                        <Input value={form.code} onChange={e=>setForm({...form,code:e.target.value.toUpperCase()})} placeholder="e.g. PROC"/>
                    </Col>
                    <Col span={12} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Category</label>
                        <Select style={{width:'100%'}} value={form.category} onChange={v=>setForm({...form,category:v})}>
                            {CATEGORY_OPTIONS.map(c=><Option key={c} value={c}>{c}</Option>)}
                        </Select>
                    </Col>
                    <Col span={12} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Tracker Group</label>
                        <Select style={{width:'100%'}} value={form.report_category} onChange={v=>setForm({...form,report_category:v})} allowClear>
                            {REPORT_CATEGORY_OPTIONS.map(c=><Option key={c} value={c}>{c}</Option>)}
                        </Select>
                    </Col>
                    <Col span={12} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Region (leave blank if national)</label>
                        <Select style={{width:'100%'}} value={form.region} onChange={v=>setForm({...form,region:v})} allowClear>
                            {REGION_OPTIONS.map(r=><Option key={r} value={r}>{r}</Option>)}
                        </Select>
                    </Col>
                    <Col span={12} style={{marginTop:10}}>
                        <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Focal Person</label>
                        <Input value={form.focal_person} onChange={e=>setForm({...form,focal_person:e.target.value})}/>
                    </Col>
                    <Col span={24} style={{marginTop:14}}>
                        <Checkbox checked={!!form.status} onChange={e=>setForm({...form,status:e.target.checked?1:0})}>Active (appears in assignment/reporting lists)</Checkbox>
                    </Col>
                </Row>
            </Modal>
        </div>
    );
}

// ── Period Assignments Tab ────────────────────────────────────────────────
function AssignmentsTab({ frameworkId }) {
    const [periods, setPeriods]         = useState([]);
    const [periodLabel, setPeriodLabel] = useState(null);
    const [units, setUnits]             = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading]         = useState(false);
    const [assigning, setAssigning]     = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch]           = useState('');
    const [syncing, setSyncing]         = useState(false);

    const loadPeriods = async () => {
        if (!frameworkId) return;
        try {
            const r = await MeService.getMePeriods(frameworkId);
            const ps = r.data?.data || [];
            setPeriods(ps);
            const cur = ps.find(p=>p.is_current==1);
            setPeriodLabel(cur ? cur.label : (ps[0]?.label || null));
        } catch { message.error('Failed to load reporting periods'); }
    };

    const loadUnits = async () => {
        try {
            const r = await ProgramsService.getPrograms({ status: 1 });
            setUnits(r.data?.data || []);
        } catch { message.error('Failed to load units'); }
    };

    const loadAssignments = async () => {
        if (!frameworkId) return;
        setLoading(true);
        try {
            const r = await MeService.getAssignments({ framework_id: frameworkId });
            setAssignments(r.data?.data || []);
        } catch { message.error('Failed to load assignments'); }
        finally { setLoading(false); }
    };

    useEffect(() => { loadPeriods(); loadUnits(); loadAssignments(); }, [frameworkId]);

    // Assignments scoped to the currently-selected period only.
    const periodAssignments = useMemo(
        () => assignments.filter(a => a.period_label === periodLabel),
        [assignments, periodLabel]
    );
    const assignedProgramIds = useMemo(
        () => new Set(periodAssignments.map(a => String(a.program_id)).filter(Boolean)),
        [periodAssignments]
    );
    const availableUnits = useMemo(
        () => units.filter(u => !assignedProgramIds.has(String(u.id)) &&
            (u.name.toLowerCase().includes(search.toLowerCase()) || u.code.toLowerCase().includes(search.toLowerCase()))),
        [units, assignedProgramIds, search]
    );

    const toggleSelect = (id) => setSelectedIds(prev =>
        prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]
    );

    const assignSelected = async () => {
        if (!selectedIds.length) { message.warning('Select at least one unit'); return; }
        const period = periods.find(p => p.label === periodLabel);
        setAssigning(true);
        try {
            const r = await MeService.bulkAssign({
                framework_id: frameworkId,
                program_ids: selectedIds,
                period_label: periodLabel,
                reporting_period: periodLabel,
                start_date: period?.period_start || moment().format('YYYY-MM-DD'),
                target_completion: period?.period_end || null,
            });
            const count = r.data?.data?.count ?? 0;
            logTrail(TRAIL_EVENTS.RECORD_CREATE, 'MoH Reporting',
                { period: periodLabel, count }, `Assigned ${count} unit(s) to ${periodLabel}`);
            message.success(`${count} unit${count!==1?'s':''} assigned to ${periodLabel}`);
            setSelectedIds([]);
            loadAssignments();
        } catch { message.error('Failed to assign units'); }
        finally { setAssigning(false); }
    };

    const unassign = async (assignment) => {
        try {
            await MeService.deleteAssignment(assignment.id);
            trailDelete('MoH Reporting', `Assignment: ${assignment.program_name}`, assignment.id, { period: periodLabel });
            message.success('Assignment removed');
            loadAssignments();
        } catch { message.error('Failed to remove assignment'); }
    };

    const runSync = async () => {
        if (!periodLabel) { message.warning('Select a reporting period first'); return; }
        setSyncing(true);
        try {
            const r = await MeService.syncAutoIndicators({ framework_id: frameworkId, period_label: periodLabel });
            const d = r.data?.data || {};
            if ((d.indicators_configured || 0) === 0) {
                message.warning('No indicators are configured with data_source = "qoc_assessment_program_score" — nothing to sync yet.');
            } else {
                message.success(`Synced ${d.synced || 0} indicator(s) from QoC assessments${d.skipped ? ` — ${d.skipped} skipped (already manually entered)` : ''}${d.units_with_no_assessment_data ? `, ${d.units_with_no_assessment_data} unit(s) had no approved assessment for this period` : ''}.`);
            }
            loadAssignments();
        } catch { message.error('Sync failed'); }
        finally { setSyncing(false); }
    };

    const cols = [
        { title:'Unit', dataIndex:'program_name', render:(n,r)=><div><div style={{fontWeight:600}}>{n}</div><div style={{fontSize:11,color:'#888'}}>{r.program_report_category||r.program_category}</div></div> },
        { title:'Status', dataIndex:'status', width:130, render:s=><Tag color={STATUS_COLORS[s]||'default'}>{(s||'').replace(/_/g,' ')}</Tag> },
        { title:'Completion', dataIndex:'completion_pct', width:110, render:p=>`${parseFloat(p||0).toFixed(0)}%` },
        { title:'HOD Sign-off', dataIndex:'supervisor_name', width:150, render:(n,r)=>{
            const vs = r.verification_status;
            if (!vs || vs==='pending') return <Tag icon={<ClockCircleOutlined/>} color="default">Pending</Tag>;
            if (vs==='verified') return <Tag icon={<CheckCircleOutlined/>} color="success">Verified</Tag>;
            if (vs==='rejected') return <Tag color="error">Rejected</Tag>;
            return <Tag>{vs}</Tag>;
        } },
        { title:'', width:70, render:(_,r)=>(
            <Popconfirm title="Remove this unit's assignment for this period?" onConfirm={()=>unassign(r)}>
                <Button size="small" danger icon={<DeleteOutlined/>}/>
            </Popconfirm>
        ) },
    ];

    return (
        <div>
            <div style={{ marginBottom:16, display:'flex', alignItems:'flex-end', gap:12 }}>
                <div>
                    <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Reporting Period</label>
                    <Select style={{width:280}} value={periodLabel} onChange={setPeriodLabel} placeholder="Select period">
                        {periods.map(p => <Option key={p.label} value={p.label}>{p.is_current==1?'★ ':''}{p.label}</Option>)}
                    </Select>
                </div>
                <Tooltip title='Pulls actual values from approved QoC assessments (via qoc_assessments.program_id) into any indicator whose Data Source is set to "qoc_assessment_program_score". Never overwrites a manually entered value.'>
                    <Button icon={<SyncOutlined spin={syncing}/>} loading={syncing} onClick={runSync}>
                        Sync from QoC Assessments
                    </Button>
                </Tooltip>
            </div>

            <Row gutter={16}>
                <Col span={11}>
                    <Card size="small" title={<span><TeamOutlined/> Available Units ({availableUnits.length})</span>}>
                        <Input placeholder="Search units..." value={search} onChange={e=>setSearch(e.target.value)} style={{marginBottom:10}} allowClear/>
                        <div style={{ maxHeight:360, overflowY:'auto' }}>
                            {availableUnits.map(u => (
                                <div key={u.id} style={{ padding:'6px 4px', borderBottom:'1px solid #f0f0f0' }}>
                                    <Checkbox checked={selectedIds.includes(u.id)} onChange={()=>toggleSelect(u.id)}>
                                        <span style={{fontWeight:600}}>{u.name}</span>
                                        <span style={{fontSize:11,color:'#888',marginLeft:6}}>{u.code}</span>
                                    </Checkbox>
                                </div>
                            ))}
                            {availableUnits.length===0 && <Empty description="All units assigned for this period"/>}
                        </div>
                    </Card>
                </Col>
                <Col span={2} style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Tooltip title="Assign selected units to this period">
                        <Button type="primary" shape="circle" icon={<ArrowRightOutlined/>}
                            disabled={!selectedIds.length} loading={assigning} onClick={assignSelected}/>
                    </Tooltip>
                </Col>
                <Col span={11}>
                    <Card size="small" title={`Assigned for ${periodLabel || '—'} (${periodAssignments.length})`}>
                        <Table columns={cols} dataSource={periodAssignments} rowKey="id" loading={loading}
                            pagination={false} size="small" scroll={{y:340}}
                            locale={{ emptyText: <Empty description="No units assigned yet for this period"/> }}/>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function MoHReportAdmin() {
    const [frameworkId, setFrameworkId] = useState(null);
    const [framework, setFramework]     = useState(null);
    const [loading, setLoading]         = useState(true);

    useEffect(() => {
        MeService.getFrameworks().then(r => {
            const fw = (r.data?.data || []).find(f => f.code === FRAMEWORK_CODE);
            if (fw) { setFramework(fw); setFrameworkId(fw.id); }
            else message.error('MoH Reporting framework not found — run the module migration first');
        }).catch(() => message.error('Failed to load framework'))
          .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{textAlign:'center',padding:60}}><Spin size="large"/></div>;

    if (!framework) {
        return (
            <div className="page-content">
                <div className="page-pad">
                    <Empty description="MoH Reporting framework not found. Run the moh_report_module_migration.sql migration first."/>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{margin:0}}>⚙️ MoH Reporting — Framework &amp; Unit Setup</h4>
                    <small className="text-muted">{framework.name} · {framework.section_count} sections · {framework.indicator_count} indicators · {framework.assignment_count} unit-period assignments</small>
                </div>
            </div>
            <div className="page-pad">
                <Tabs
                    defaultActiveKey="indicators"
                    items={[
                        { key:'indicators',  label:'Indicator Catalog',    children:<IndicatorCatalogTab frameworkId={frameworkId}/> },
                        { key:'units',       label:'Submitting Units',     children:<SubmittingUnitsTab/> },
                        { key:'assignments', label:'Period Assignments',   children:<AssignmentsTab frameworkId={frameworkId}/> },
                    ]}
                />
            </div>
        </div>
    );
}